import React, { useRef, useState, useEffect } from "react";
import { CardHeader } from "@/components/ui/card";
import { Chip } from "@/components/common/Chip";
import { cn } from "@/lib/utils";
import { Typography } from "@/components/ui/typography";
import { TooltipProvider } from "@radix-ui/react-tooltip";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

// Identical props to DashboardCardHeader — used only for EditLayout dummy/preview cards.
type EditLayoutCardHeaderProps = {
  icon?: React.ReactNode;
  title: React.ReactNode;
  chip?: {
    key: string | React.ReactNode;
    value: string | number;
    variant?: "default" | "success" | "warning" | "error" | "danger";
    onClick?: () => void;
  };
  showStar?: boolean;
  button?: React.ReactNode;
  isFavourite?: boolean;
  onToggleFavourite?: () => void;
  className?: string;
};

const EditLayoutCardHeader: React.FC<EditLayoutCardHeaderProps> = ({
  icon,
  title,
  chip,
  showStar,
  button,
  isFavourite = false,
  onToggleFavourite,
  className,
}) => {
  const borderColorClass = isFavourite
    ? "border-[hsl(var(--dashboard-header-favourite-border))]"
    : "border-grey-200 dark:border-grey-700";

  // containerRef is placed on the stable outer wrapper that always exists regardless
  // of whether the tooltip branch is active. Observing it (not the text node) means
  // the ResizeObserver is never watching a remounted/detached element.
  const containerRef = useRef<HTMLDivElement>(null);
  // textRef is read fresh inside checkOverflow — never captured in a closure.
  const textRef = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const checkOverflow = () => {
      // Always read textRef.current at call time, not at setup time, so we never
      // hold a reference to a detached DOM node.
      const el = textRef.current;
      if (el) {
        setIsOverflowing(el.scrollWidth > el.clientWidth);
      }
    };

    checkOverflow();

    // Observe the outer container (stable across re-renders) so that any
    // layout-driven width change — drawer open/close, column count change —
    // triggers an overflow re-check without relying on window resize events.
    const observer = new ResizeObserver(checkOverflow);
    observer.observe(container);

    return () => observer.disconnect();
  }, [title]);

  return (
    <CardHeader
      className={cn(
        "flex flex-row items-center justify-between px-4 py-3 border-b rounded-t-lg",
        "bg-[hsl(var(--navbar))] dark:bg-[hsl(var(--blue-800))]",
        borderColorClass,
        className
      )}>
      {/* Left side: stable container always rendered; only the inner text switches
          between plain and tooltip-wrapped to avoid remounting the container itself. */}
      <div ref={containerRef} className="flex items-center gap-2 min-w-0 flex-1">
        {icon && <div className="text-blue-500 flex-shrink-0">{icon}</div>}
        {isOverflowing ? (
          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Typography ref={textRef} variant="semibold-16" className="truncate cursor-pointer">
                  {title}
                </Typography>
              </TooltipTrigger>
              <TooltipContent
                side="top"
                align="start"
                className="max-w-xs bg-gray-700 text-grey-100 break-words z-50 [&_*]:!text-grey-100">
                <Typography variant="regular-12" className="text-grey-100">
                  {title}
                </Typography>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <Typography ref={textRef} variant="semibold-16" className="truncate">
            {title}
          </Typography>
        )}
      </div>

      {/* Right side: button + star + chip */}
      <div className="flex items-center gap-4 mb-[3px] overflow-hidden flex-shrink-0 max-w-[50%]">
        {chip && (
          <Chip
            label={chip.key}
            value={chip.value}
            variant={chip.variant || "default"}
            onClick={chip.onClick}
          />
        )}
        {button}
        {showStar && (
          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onToggleFavourite) {
                      onToggleFavourite();
                    }
                  }}
                  aria-pressed={isFavourite}
                  className={cn(
                    "p-1 transition-colors bg-transparent cursor-pointer",
                    isFavourite
                      ? "text-[hsl(var(--dashboard-header-favourite-text))]"
                      : "text-grey-800 dark:text-grey-100 hover:text-warning dark:hover:text-warning"
                  )}>
                  <i
                    className={cn(
                      "text-[18px]",
                      isFavourite ? "ri-star-fill" : "ri-star-line"
                    )}
                  />
                </button>
              </TooltipTrigger>
              <TooltipContent
                side="top"
                align="end"
                className="max-w-xs bg-gray-700 text-grey-100 break-words z-50">
                <Typography variant="regular-12" className="text-grey-100">
                  {isFavourite ? "Remove from favourites" : "Mark as favourite"}
                </Typography>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
    </CardHeader>
  );
};

export default EditLayoutCardHeader;
